<template>
  <div class="w-full h-full space-y-4">
    <Card>
      <CardHeader>
        <CardTitle>Whitelabel sites</CardTitle>
        <CardDescription>
          Create and manage your rewards sites. Each site has its own domain, branding and offers,
          and can share a
          <router-link to="/whitelabel/themes" class="underline">theme</router-link>.
        </CardDescription>
        <CardAction>
          <Button size="sm" :disabled="creating" @click="createSite">
            <Plus class="w-4 h-4" /> {{ creating ? 'Creating…' : 'New site' }}
          </Button>
        </CardAction>
      </CardHeader>
    </Card>

    <div v-if="loading" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Skeleton class="h-40" />
      <Skeleton class="h-40" />
      <Skeleton class="h-40" />
    </div>

    <Card v-else-if="!sites.length">
      <CardContent class="py-16 text-center space-y-3">
        <div class="mx-auto flex size-12 items-center justify-center rounded-full bg-muted">
          <LayoutGrid class="w-6 h-6 text-muted-foreground" />
        </div>
        <div>
          <p class="font-medium">No sites yet</p>
          <p class="text-sm text-muted-foreground">
            Create your first whitelabel site to get started.
          </p>
        </div>
        <div class="flex items-center justify-center gap-2">
          <Button size="sm" :disabled="creating" @click="createSite">
            <Plus class="w-4 h-4" /> New site
          </Button>
        </div>
      </CardContent>
    </Card>

    <div v-else class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Card v-for="site in sites" :key="site.id" class="flex flex-col">
        <CardHeader>
          <CardTitle class="text-base truncate">{{ site.name || 'Untitled site' }}</CardTitle>
          <CardDescription class="truncate">{{ siteHost(site) }}</CardDescription>
          <CardAction>
            <Badge v-if="site.published" variant="success">Published</Badge>
            <Badge v-else variant="secondary">Draft</Badge>
          </CardAction>
        </CardHeader>
        <CardContent class="mt-auto flex items-center gap-2">
          <Button variant="outline" size="sm" @click="edit(site)">
            <Pencil class="w-4 h-4" /> Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            class="text-destructive"
            :disabled="deleting === site.id"
            @click="confirmDelete(site)"
          >
            <Trash2 class="w-4 h-4" /> {{ deleting === site.id ? 'Deleting…' : 'Delete' }}
          </Button>
        </CardContent>
      </Card>
    </div>

    <AlertDialog
      :open="deleteOpen"
      @update:open="
        (v) => {
          deleteOpen = v
        }
      "
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this site?</AlertDialogTitle>
          <AlertDialogDescription>
            “{{ deleteTarget && deleteTarget.name }}” and its published site will be permanently
            removed. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel @click="deleteOpen = false">Cancel</AlertDialogCancel>
          <AlertDialogAction
            class="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            @click="doDelete"
            >Delete</AlertDialogAction
          >
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
  Button,
  Badge,
  Skeleton,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  toast,
} from '@boogi/ui'
import { Plus, Pencil, Trash2, LayoutGrid } from 'lucide-vue-next'

export default {
  name: 'WhitelabelSites',
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    Badge,
    Skeleton,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
    Plus,
    Pencil,
    Trash2,
    LayoutGrid,
  },
  data() {
    return {
      loading: true,
      creating: false,
      deleting: null,
      deleteOpen: false,
      deleteTarget: null,
      sites: [],
    }
  },
  created() {
    this.load()
  },
  mounted() {
    document.title = 'Dashboard | Whitelabel sites'
  },
  methods: {
    base() {
      return localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
    },
    authHeaders(json = true) {
      const h = { Authorization: `${localStorage.getItem('token')}` }
      if (json) h['Content-Type'] = 'application/json'
      return h
    },
    api(path = '') {
      return `${this.base()}/private/api/client/whitelabel/sites${path}`
    },
    siteHost(site) {
      const d = site.domain || {}
      return d.customDomain || 'No domain set'
    },
    async load() {
      this.loading = true
      try {
        const res = await this.$apiFetch(this.api(), { method: 'GET', headers: this.authHeaders() })
        if (res.ok) this.sites = await res.json()
        else toast.error('Could not load sites.')
      } catch (e) {
        toast.error('Could not load sites.')
      } finally {
        this.loading = false
      }
    },
    async createSite() {
      this.creating = true
      try {
        const res = await this.$apiFetch(this.api(), {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify({ name: 'Untitled site' }),
        })
        if (res.ok) {
          const site = await res.json()
          this.$router.push(`/whitelabel/sites/${site.id}`)
        } else toast.error('Could not create site.')
      } catch (e) {
        toast.error('Could not create site.')
      } finally {
        this.creating = false
      }
    },
    edit(site) {
      this.$router.push(`/whitelabel/sites/${site.id}`)
    },
    confirmDelete(site) {
      this.deleteTarget = site
      this.deleteOpen = true
    },
    async doDelete() {
      const site = this.deleteTarget
      this.deleteOpen = false
      if (!site) return
      this.deleting = site.id
      try {
        const res = await this.$apiFetch(this.api(`/${site.id}`), {
          method: 'DELETE',
          headers: this.authHeaders(),
        })
        if (res.ok) {
          this.sites = this.sites.filter((s) => s.id !== site.id)
          toast.success('Site deleted')
        } else {
          const body = await res.text().catch(() => '')
          console.error('delete site failed', res.status, body)
          toast.error(`Could not delete site (${res.status}).`)
        }
      } catch (e) {
        toast.error('Could not delete site.')
      } finally {
        this.deleting = null
        this.deleteTarget = null
      }
    },
  },
}
</script>
